// Implements: F-IDN-012
/**
 * The signed-in person as the account pages and the user menu read them (mockup v3 P1).
 *
 * `useMeQuery` is `GET /auth/me` under `["account","me"]`, asked only while a session exists.
 * `useMe` is what the user menu needs: the profile once it is read, and the session's own user with
 * no photo until then (or when the read failed), so the menu never waits on it nor goes blank.
 */

import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import { useSession } from "../../../shared/api/session";
import type { MeSummary } from "../../../types/api";
import { ACCOUNT_ME_KEY, fetchMe } from "../api/accountApi";

/** What the user menu needs of `MeSummary`. */
export type MeView = Pick<MeSummary, "id" | "email" | "full_name" | "avatar_url">;

/** `active: false` keeps the read off (a section whose person is being erased never asks again). */
export function useMeQuery(active = true): UseQueryResult<MeSummary, ApiError> {
  const { state } = useSession();
  return useQuery<MeSummary, ApiError>({
    queryKey: ACCOUNT_ME_KEY,
    queryFn: ({ signal }) => fetchMe(signal),
    enabled: active && state.status === "authenticated",
  });
}

export function useMe(): MeView | null {
  const { state } = useSession();
  const me = useMeQuery();
  if (state.status !== "authenticated") return null;
  if (me.data !== undefined) return me.data;
  const { id, email, full_name } = state.view.user;
  return { id, email, full_name, avatar_url: null };
}
